import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  /** 0-based 페이지 인덱스 */
  pageIndex: number;
  totalPages: number;
  totalElements?: number;
  pageSize?: number;
  onPageChange: (pageIndex: number) => void;
  /** 가운데에 표시할 페이지 버튼 수 (기본 5) */
  windowSize?: number;
}

/**
 * 페이지네이션 (0-based pageIndex)
 * - 처음/이전/페이지 번호/다음/마지막
 * - 모바일에서는 번호 버튼 축소
 */
export function Pagination({
  pageIndex,
  totalPages,
  totalElements,
  pageSize,
  onPageChange,
  windowSize = 5,
}: PaginationProps) {
  if (totalPages <= 0) return null;

  const currentPage = pageIndex + 1;
  const half = Math.floor(windowSize / 2);

  let start = Math.max(1, currentPage - half);
  const end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const canPrev = pageIndex > 0;
  const canNext = pageIndex < totalPages - 1;

  const baseBtn =
    'inline-flex h-9 min-w-[36px] items-center justify-center rounded-lg border border-gray-300 bg-white px-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <div className="flex flex-col items-center justify-between gap-2 sm:flex-row">
      <div className="text-xs text-gray-600 sm:text-sm">
        {totalElements != null && pageSize ? (
          <>
            전체 <strong className="text-gray-900">{totalElements.toLocaleString()}</strong>건 · {currentPage}/{totalPages} 페이지
          </>
        ) : (
          <>
            {currentPage}/{totalPages} 페이지
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(0)}
          disabled={!canPrev}
          className={baseBtn}
          aria-label="처음 페이지"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={!canPrev}
          className={baseBtn}
          aria-label="이전 페이지"
        >
          <ChevronLeft size={16} />
        </button>

        {pages.map(p => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p - 1)}
            className={`${baseBtn} ${
              p === currentPage
                ? '!border-primary-500 !bg-primary-500 !text-white'
                : ''
            }`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={!canNext}
          className={baseBtn}
          aria-label="다음 페이지"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={!canNext}
          className={baseBtn}
          aria-label="마지막 페이지"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
}
